import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import axios from 'axios';
import Header from '../components/Header';
import Footer from '../components/Footer';
import { useAuth } from '../context/AuthContext';
import { ArrowLeft, AlertTriangle, DollarSign, Clock, Users, FileText, Palette } from 'lucide-react';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API_BASE_URL = `${BACKEND_URL}/api`;

const DeleteService = () => {
  const { token, getAuthHeaders, isAdmin } = useAuth();
  const navigate = useNavigate();
  const { serviceId } = useParams();
  const [service, setService] = useState(null);
  const [loading, setLoading] = useState(true);
  const [deleting, setDeleting] = useState(false);
  const [error, setError] = useState('');

  const fetchService = useCallback(async () => {
    try {
      setLoading(true);
      const response = await axios.get(`${API_BASE_URL}/services/${serviceId}`, {
        headers: getAuthHeaders()
      });
      setService(response.data.data);
    } catch (err) {
      console.error('Error fetching service:', err);
      setError('Failed to load service details');
    } finally {
      setLoading(false);
    }
  }, [serviceId, token]);

  useEffect(() => {
    if (!isAdmin()) {
      navigate('/dashboard');
      return;
    }
    fetchService();
  }, [isAdmin, navigate, fetchService]);

  const handleDelete = async () => {
    try {
      setDeleting(true);
      setError('');

      await axios.delete(`${API_BASE_URL}/services/${serviceId}`, {
        headers: getAuthHeaders()
      });

      // Redirect to manage services page after successful deletion
      navigate('/admin/services', { 
        state: { 
          message: `Service "${service.service_name}" has been deleted successfully.` 
        } 
      });
    } catch (err) {
      setError(err.response?.data?.detail || 'Failed to delete service');
      setDeleting(false);
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen">
        <Header />
        <div className="flex items-center justify-center py-20">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-yellow-500"></div>
        </div>
      </div>
    );
  }

  if (!service) {
    return (
      <div className="min-h-screen">
        <Header />
        <div className="max-w-4xl mx-auto px-6 py-12">
          <div className="text-center">
            <h1 className="text-2xl font-bold text-gray-800 mb-4">Service Not Found</h1>
            <button
              onClick={() => navigate('/admin/services')}
              className="text-yellow-500 hover:text-yellow-600"
            >
              Return to Manage Services
            </button>
          </div>
        </div>
        <Footer />
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-50">
      <Header />
      <section className="py-12">
        <div className="max-w-4xl mx-auto px-6">
          <button
            onClick={() => navigate('/admin/services')}
            className="mb-6 flex items-center text-gray-600 hover:text-gray-800 transition-colors"
          >
            <ArrowLeft className="w-5 h-5 mr-2" />
            Back to Manage Services
          </button>

          {error && (
            <div className="mb-6 p-4 bg-red-100 border border-red-400 text-red-700 rounded">
              {error}
            </div>
          )}

          {/* Warning Banner */}
          <div className="bg-red-50 border-l-4 border-red-500 p-6 mb-6">
            <div className="flex items-center">
              <AlertTriangle className="w-8 h-8 text-red-500 mr-4" />
              <div>
                <h2 className="text-xl font-bold text-red-800 mb-1">Delete Service</h2>
                <p className="text-red-700">
                  You are about to permanently delete this service. This action cannot be undone.
                </p>
              </div>
            </div>
          </div>

          {/* Service Details Card */}
          <div className="bg-white rounded-lg shadow-md p-8 mb-6">
            <h3 className="text-2xl font-bold text-gray-800 mb-6">Service Information</h3>
            
            <div className="space-y-4">
              <div className="flex items-start border-b border-gray-200 pb-4">
                <FileText className="w-6 h-6 text-gray-400 mr-4 mt-1" />
                <div className="flex-1">
                  <p className="text-sm text-gray-600 mb-1">Service Name</p>
                  <p className="text-lg font-semibold text-gray-900">{service.service_name}</p>
                </div>
              </div>

              <div className="flex items-start border-b border-gray-200 pb-4">
                <DollarSign className="w-6 h-6 text-gray-400 mr-4 mt-1" />
                <div className="flex-1">
                  <p className="text-sm text-gray-600 mb-1">Price</p>
                  <p className="text-lg font-semibold text-gray-900">${service.service_price}</p>
                </div>
              </div>

              <div className="flex items-start border-b border-gray-200 pb-4">
                <Clock className="w-6 h-6 text-gray-400 mr-4 mt-1" />
                <div className="flex-1">
                  <p className="text-sm text-gray-600 mb-1">Duration</p>
                  <p className="text-lg font-semibold text-gray-900">{service.service_duration} minutes</p>
                </div>
              </div>

              <div className="flex items-start border-b border-gray-200 pb-4">
                <Users className="w-6 h-6 text-gray-400 mr-4 mt-1" />
                <div className="flex-1">
                  <p className="text-sm text-gray-600 mb-1">Max Customers</p>
                  <p className="text-lg font-semibold text-gray-900">{service.customers_allowed}</p>
                </div>
              </div>

              {service.service_description && (
                <div className="flex items-start border-b border-gray-200 pb-4">
                  <FileText className="w-6 h-6 text-gray-400 mr-4 mt-1" />
                  <div className="flex-1">
                    <p className="text-sm text-gray-600 mb-1">Description</p>
                    <p className="text-lg font-semibold text-gray-900">{service.service_description}</p>
                  </div>
                </div>
              )}

              {service.color_code && (
                <div className="flex items-start pb-4">
                  <Palette className="w-6 h-6 text-gray-400 mr-4 mt-1" />
                  <div className="flex-1">
                    <p className="text-sm text-gray-600 mb-1">Color Code</p>
                    <div className="flex items-center">
                      <div 
                        className="w-8 h-8 rounded border border-gray-300 mr-3"
                        style={{ backgroundColor: service.color_code }}
                      ></div>
                      <p className="text-lg font-semibold text-gray-900">{service.color_code}</p>
                    </div>
                  </div>
                </div>
              )}
            </div>
          </div>

          {/* Warning Info */}
          <div className="bg-yellow-50 border border-yellow-200 rounded-lg p-6 mb-6">
            <h4 className="font-semibold text-yellow-800 mb-3">What will be deleted:</h4>
            <ul className="list-disc list-inside space-y-2 text-yellow-700">
              <li>Service details and configuration</li>
              <li>Service pricing information</li>
              <li>All associations with calendars using this service</li>
              <li>Service will be removed from all future bookings</li>
            </ul>
            <p className="mt-3 text-sm text-yellow-800 font-medium">
              Note: Existing appointments using this service will not be affected.
            </p>
          </div>

          {/* Action Buttons */}
          <div className="flex flex-col sm:flex-row gap-4">
            <button
              onClick={() => navigate('/admin/services')}
              className="flex-1 bg-gray-200 hover:bg-gray-300 text-gray-800 font-semibold py-4 px-6 rounded-md transition-colors"
            >
              Cancel - Keep Service
            </button>
            <button
              onClick={handleDelete}
              disabled={deleting}
              className="flex-1 bg-red-600 hover:bg-red-700 text-white font-semibold py-4 px-6 rounded-md transition-colors disabled:opacity-50 disabled:cursor-not-allowed flex items-center justify-center"
            >
              {deleting ? (
                <>
                  <div className="animate-spin rounded-full h-5 w-5 border-b-2 border-white mr-2"></div>
                  Deleting...
                </>
              ) : (
                <>
                  <AlertTriangle className="w-5 h-5 mr-2" />
                  Confirm Delete Service
                </>
              )}
            </button>
          </div>
        </div>
      </section>
      <Footer />
    </div>
  );
};

export default DeleteService;
